import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Textarea, 
  Label, 
  FormGroup, 
  ErrorMessage 
} from '../../../../styles/components/common/forms';
import { 
  Button, 
  SecondaryButton 
} from '../../../../styles/components/common/buttons';
import { glassDeep, glassMedium } from '../../../../styles/components/common/glassmorphism';

const AppealContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 1.5rem 0;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const AppealHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  h3 {
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-size: 1.5rem;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 600;
  }
  
  button {
    background: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);
    
    &:hover {
      color: rgba(255, 255, 255, 0.9);
    }
  }
`;

const ReportInfoCard = styled.div`
  ${glassMedium}
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid ${props => props.statusColor};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  }
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  h4 {
    margin: 0;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const StatusBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: ${props => `${props.color}30`};
  color: ${props => props.color};
`;

const DateInfo = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 0.5rem 0;
`;

const FacultyFeedback = styled.div`
  ${glassMedium}
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid ${props => props.statusColor};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  }
  
  h4 {
    margin: 0 0 0.75rem 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.1rem;
  }
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.5;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column-reverse;
    gap: 1rem;
  }
  
  button {
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }
  }
`;

const CharacterCount = styled.small`
  display: block;
  color: ${props => props.isError ? 'rgba(239, 68, 68, 0.8)' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 0.75rem;
  margin-top: 0.25rem;
  text-align: right;
`;

const AppealForm = ({ report, onClose, onSubmit, existingAppeal }) => {
  const [appealMessage, setAppealMessage] = useState(existingAppeal || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (existingAppeal) {
      setAppealMessage(existingAppeal);
    }
  }, [existingAppeal]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!appealMessage.trim()) {
      setError('Please provide an appeal message.');
      return;
    }
    
    if (appealMessage.length < 50) {
      setError('Your appeal message is too short. Please provide more details (minimum 50 characters).');
      return;
    }
    
    onSubmit(appealMessage);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'flagged': return 'rgba(245, 158, 11, 0.9)'; // Amber
      case 'rejected': return 'rgba(239, 68, 68, 0.9)'; // Red
      default: return 'rgba(99, 102, 241, 0.9)'; // Indigo
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'flagged': return 'Needs Revision';
      case 'rejected': return 'Rejected';
      case 'under_appeal': return 'Under Appeal';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  return (
    <AppealContainer>
      <AppealHeader>
        <h3>Appeal Report Decision</h3>
        <button onClick={onClose}>&times;</button>
      </AppealHeader>

      <ReportInfoCard statusColor={getStatusColor(report.status)}>
        <ReportHeader>
          <h4>{report.title}</h4>
          <StatusBadge color={getStatusColor(report.status)}>
            {getStatusLabel(report.status)}
          </StatusBadge>
        </ReportHeader>
        <DateInfo>
          Submitted on {new Date(report.submissionDate).toLocaleDateString()}
        </DateInfo>
      </ReportInfoCard>

      {/* Show faculty feedback or rejection reason */}
      {report.rejectionReason && (
        <FacultyFeedback statusColor={getStatusColor(report.status)}>
          <h4>{report.status === 'flagged' ? 'Revision Reason' : 'Rejection Reason'}:</h4>
          <p>{report.rejectionReason}</p>
        </FacultyFeedback>
      )}

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="appealMessage">Appeal Message</Label>
          <Textarea
            id="appealMessage"
            value={appealMessage}
            onChange={(e) => {
              setAppealMessage(e.target.value);
              if (error) setError('');
            }}
            placeholder="Explain why you believe this decision should be reconsidered..."
            rows={6}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <CharacterCount isError={appealMessage.length < 50}>
            Minimum 50 characters. Current: {appealMessage.length}
          </CharacterCount>
        </FormGroup>

        <FormActions>
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <Button type="submit">
            Submit Appeal
          </Button>
        </FormActions>
      </form>
    </AppealContainer>
  );
};

export default AppealForm;